import { api } from './api';
import type {
  ApiEnvelope, AuditFields, EngineListParams, PaginatedEnvelope,
} from './notification-engine.types';

const BASE = '/api/v1/notification-type-masters';

/**
 * A notification_type_master row — the class a notification belongs to, and the
 * delivery policy every notification of that class inherits.
 *
 * Channels, retry, expiry and the SLA deadline sit here rather than on the
 * notification: change them on the type and every notification of it follows.
 */
export interface NotificationType extends AuditFields {
  _id: string;
  notificationCode: string;
  description?: string | null;
  category: string;                 // TRANSACTIONAL | PROMOTIONAL | SYSTEM | ALERT
  priority?: string | null;         // LOW | NORMAL | HIGH | URGENT
  channels: string[];
  isChannelFallbackEnabled?: boolean;
  /**
   * Retry PACING only - `{backoff, intervalSeconds}`. There is no attempt cap:
   * a failed delivery is retried until it arrives. `maxAttempts` is rejected by
   * the API rather than ignored, so a type cannot claim a limit the engine does
   * not keep.
   */
  retryPolicy?: { backoff?: 'FIXED' | 'EXPONENTIAL'; intervalSeconds?: number } | null;
  expiresAfterSeconds?: number | null;
  retentionDays?: number | null;
  /** Minutes a recipient has to act before the delivery is in breach. */
  slaMinutes?: number | null;
  /** `false` makes the class unopt-outable — a person's opt-out is ignored. */
  isUserOptOutAllowed?: boolean;
  isQuietHoursRespected?: boolean;
}

export type NotificationTypeCreateRequest =
  Omit<NotificationType, '_id' | keyof AuditFields> & { appId?: string | null };

export type NotificationTypeUpdateRequest = Partial<NotificationTypeCreateRequest>;

export const notificationTypesService = {
  list: async (params?: EngineListParams): Promise<PaginatedEnvelope<NotificationType>> => {
    const response = await api.get<PaginatedEnvelope<NotificationType>>(BASE, { params });
    return response.data;
  },
  getById: async (id: string): Promise<NotificationType> => {
    const response = await api.get<ApiEnvelope<NotificationType>>(`${BASE}/${id}`);
    return response.data.data;
  },
  create: async (data: NotificationTypeCreateRequest): Promise<NotificationType> => {
    const response = await api.post<ApiEnvelope<NotificationType>>(BASE, data);
    return response.data.data;
  },
  update: async (id: string, data: NotificationTypeUpdateRequest): Promise<NotificationType> => {
    const response = await api.put<ApiEnvelope<NotificationType>>(`${BASE}/${id}`, data);
    return response.data.data;
  },
  /** Retire without deleting: the runtime reads only active rows. */
  deactivate: async (id: string): Promise<NotificationType> => {
    const response = await api.post<ApiEnvelope<NotificationType>>(`${BASE}/${id}/deactivate`);
    return response.data.data;
  },
  softDelete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<ApiEnvelope<{ message: string }>>(`${BASE}/${id}`);
    return response.data.data;
  },
};
